import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { DeleteOutlined, EditOutlined, FileSearchOutlined } from '@ant-design/icons';

import { useDataGridTextFilter } from '../DataGrid/filters/useDataGridTextFilter.jsx';
import { dataGridTextSorter } from '../DataGrid/sorters/DataGridSorters.jsx';
import DataGrid from '../DataGrid/DataGrid.jsx';
import { Spinner } from '../index.jsx';
import IconButton from '../controls/IconButton/IconButton.jsx';

const IngProjectsMyApplicationsTable = ({
  applications,
  canModifyApplications = true,
  disabledTooltip,
  onDetailClick,
  onEditClick,
  onDeleteClick,
}) => {
  const { t } = useTranslation();

  const projectNameFilterProps = useDataGridTextFilter(['project', 'name']);
  const studyProgramFilterProps = useDataGridTextFilter(['study_program', 'name']);

  const columns = useMemo(
    () => [
      {
        title: t('Priorita'),
        dataIndex: 'priority',
        width: 100,
        sorter: (a, b) => (a.priority || 0) - (b.priority || 0),
        defaultSortOrder: 'ascend',
      },
      {
        title: t('Názov projektu'),
        dataIndex: ['project', 'name'],
        ellipsis: true,
        width: '35%',
        sorter: dataGridTextSorter(['project', 'name']),
        ...projectNameFilterProps,
      },
      {
        title: t('Študijný program'),
        dataIndex: ['study_program', 'name'],
        ellipsis: true,
        width: '20%',
        sorter: dataGridTextSorter(['study_program', 'name']),
        ...studyProgramFilterProps,
      },
      {
        title: t('Priorita učiteľa'),
        dataIndex: 'teacher_priority',
        width: 130,
        sorter: (a, b) => (a.teacher_priority || 0) - (b.teacher_priority || 0),
        render: (value) => value || '–',
      },
    ],
    [t, projectNameFilterProps, studyProgramFilterProps],
  );

  if (!applications) {
    return <Spinner />;
  }

  return (
    <DataGrid
      rowKey={(record) => record.id}
      columns={columns}
      dataSource={applications}
      onRowDetailClick={(record) => onDetailClick?.(record.project)}
      optionsWidth={130}
      customSettings={(record) => (
        <div style={{ display: 'flex' }}>
          <IconButton
            icon={<EditOutlined />}
            tooltip={t('Upraviť prihlášku')}
            onClick={() => onEditClick?.(record)}
            disabled={!canModifyApplications}
            disabledTooltip={disabledTooltip || t('Úprava prihlášok nie je v tomto období povolená')}
          />
          <IconButton
            icon={<FileSearchOutlined />}
            tooltip={t('Detail projektu')}
            onClick={() => onDetailClick?.(record.project)}
          />
          <IconButton
            icon={<DeleteOutlined />}
            tooltip={t('Zmazať prihlášku')}
            onClick={() => onDeleteClick?.(record)}
            disabled={!canModifyApplications}
            disabledTooltip={disabledTooltip || t('Mazanie prihlášok nie je v tomto období povolené')}
          />
        </div>
      )}
    />
  );
};

IngProjectsMyApplicationsTable.propTypes = {
  applications: PropTypes.array,
  canModifyApplications: PropTypes.bool,
  disabledTooltip: PropTypes.string,
  onDetailClick: PropTypes.func,
  onEditClick: PropTypes.func,
  onDeleteClick: PropTypes.func,
};

export default IngProjectsMyApplicationsTable;
